import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { SetAssetGroupTool } from 'js/src/agent/tools/SetAssetGroupTool';
import { SetTextPositionPresetTool } from 'js/src/agent/tools/SetTextPositionPresetTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';

export const fixTextWrappingSkill: Skill = {
    name: SKILL_NAMES.fixTextWrapping,
    description:
        'Fixing widows, orphans, and awkward line wraps in text assets. Load when a tool finding reports a wrap problem.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Fixing Text Wrapping

A widow is a rendered line holding a single stranded word:

> Then the coffee is ground into a
> powder

If you get reports of text widows or orphaned lines, here's how to fix.

## Fix ladder (in order)
1. **Too much text?** This is the usual root cause. If the text wraps to three-plus
   lines or strands a word, the real fix is *less text in this scene* — split it
   into more overlays/subscenes at a natural break — not making a long block wrap
   nicely. Aim for one or two balanced lines.
2. **Text is too large or too small**
3. **Reword to balance the lines**
4. **Adjust the box width.** A stranded last word means the width lets exactly one
   word tip over: *widen* slightly (pulls the previous line's last word down so the
   final line has 2+ words) or *narrow* (pushes more words down together)

## Supporting moves
- **Stacked blocks** (eyebrow + heading + subtitle): when the group as a whole is
  mispositioned or cramped and nudging individual boxes isn't working, control the
  group with \`${SetAssetGroupTool.toolName}\` (\`position\`, \`max_height\`, \`align_children\`).
- **Moving text**: reach for \`${SetTextPositionPresetTool.toolName}\` first (check the scene's
  \`textPositionPresets\`); manual coordinates only when no preset fits.
- **Shape-bounded assets** (circle, badge, pill): the shape can't grow. Either put a
  one-or-two-word label in it or switch to a design whose text area fits the phrase.

## Confirm
After each change, check the output of ${SetTextTool.toolName} to see how many lines are being rendered and repeat
until the issue is fixed. Don't sign off on a box with a widow.`;
    },
};
